import { useRef, useState } from "react";
import { useEditor, EditorContent, useEditorState } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Image from "@tiptap/extension-image";
import Youtube from "@tiptap/extension-youtube";
import TextAlign from "@tiptap/extension-text-align";
import Highlight from "@tiptap/extension-highlight";
import { TextStyleKit } from "@tiptap/extension-text-style";
import { TableKit } from "@tiptap/extension-table";
import {
  Bold, Italic, Underline, Strikethrough, List, ListOrdered, Quote, Link2, ImagePlus, Video as YtIcon, Undo2, Redo2,
  AlignLeft, AlignCenter, AlignRight, AlignJustify, Highlighter, Minus, Table as TableIcon, Eraser, Code, Rows3, Columns3, Trash2,
} from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "../../lib/api";
import { FONTS, loadFonts } from "../../lib/fonts";
import { Field, LangTabs, useTranslate } from "./Fields";

const SIZES = ["12px", "14px", "16px", "18px", "20px", "24px", "30px", "36px", "48px"];
const sel = "h-8 border rounded px-1.5 text-xs bg-white";

function Toolbar({ editor, testid, onImage }) {
  const s = useEditorState({
    editor,
    selector: ({ editor: e }) => ({
      block: e.isActive("heading", { level: 1 }) ? "h1" : e.isActive("heading", { level: 2 }) ? "h2" : e.isActive("heading", { level: 3 }) ? "h3" : e.isActive("heading", { level: 4 }) ? "h4" : "p",
      font: e.getAttributes("textStyle").fontFamily || "", size: e.getAttributes("textStyle").fontSize || "", color: e.getAttributes("textStyle").color || "#000000",
      bold: e.isActive("bold"), italic: e.isActive("italic"), underline: e.isActive("underline"), strike: e.isActive("strike"), code: e.isActive("code"),
      hl: e.isActive("highlight"), ul: e.isActive("bulletList"), ol: e.isActive("orderedList"), quote: e.isActive("blockquote"), link: e.isActive("link"), table: e.isActive("table"),
      align: ["center", "right", "justify"].find((a) => e.isActive({ textAlign: a })) || "left",
    }),
  });
  const c = () => editor.chain().focus();
  const btn = (active, run, Icon, title) => (
    <button type="button" title={title} onClick={run} data-testid={`${testid}-tool-${title}`}
      className={`p-1.5 rounded transition-colors hover:bg-stone-200 ${active ? "bg-stone-900 text-white hover:bg-stone-800" : "text-stone-600"}`}><Icon size={15} /></button>
  );
  const sep = <span className="w-px h-6 bg-stone-300 mx-1" />;
  const setBlock = (v) => (v === "p" ? c().setParagraph().run() : c().setHeading({ level: Number(v[1]) }).run());
  const setFont = (v) => { if (v) loadFonts([v], `font-${v}`); v ? c().setFontFamily(`'${v}'`).run() : c().unsetFontFamily().run(); };
  const addLink = () => {
    const url = window.prompt("Link adresi (https://… veya /sayfa)", editor.getAttributes("link").href || "");
    if (url === null) return;
    if (!url) return c().extendMarkRange("link").unsetLink().run();
    c().extendMarkRange("link").setLink({ href: url }).run();
  };
  const addYoutube = () => { const url = window.prompt("YouTube video linki"); if (url) editor.commands.setYoutubeVideo({ src: url }); };
  return (
    <div className="flex flex-wrap items-center gap-0.5 border-b bg-stone-50 p-1.5 sticky top-0 z-10" data-testid={`${testid}-toolbar`}>
      <select className={sel} value={s.block} onChange={(e) => setBlock(e.target.value)} data-testid={`${testid}-block`}>
        <option value="p">Paragraf</option><option value="h1">Başlık 1</option><option value="h2">Başlık 2</option><option value="h3">Başlık 3</option><option value="h4">Başlık 4</option>
      </select>
      <select className={`${sel} max-w-[130px]`} value={s.font.replace(/'/g, "")} onChange={(e) => setFont(e.target.value)} data-testid={`${testid}-font`}>
        <option value="">Yazı tipi</option>{FONTS.map((f) => <option key={f} value={f}>{f}</option>)}
      </select>
      <select className={sel} value={s.size} onChange={(e) => (e.target.value ? c().setFontSize(e.target.value).run() : c().unsetFontSize().run())} data-testid={`${testid}-size`}>
        <option value="">Boyut</option>{SIZES.map((x) => <option key={x} value={x}>{parseInt(x, 10)}</option>)}
      </select>
      {sep}
      {btn(s.bold, () => c().toggleBold().run(), Bold, "bold")}
      {btn(s.italic, () => c().toggleItalic().run(), Italic, "italic")}
      {btn(s.underline, () => c().toggleUnderline().run(), Underline, "underline")}
      {btn(s.strike, () => c().toggleStrike().run(), Strikethrough, "strike")}
      {btn(s.code, () => c().toggleCode().run(), Code, "code")}
      <label title="Yazı rengi" className="relative p-1.5 rounded hover:bg-stone-200 cursor-pointer flex items-center" data-testid={`${testid}-tool-color`}>
        <span className="text-[13px] font-bold leading-none" style={{ color: s.color, borderBottom: `3px solid ${s.color}` }}>A</span>
        <input type="color" value={s.color.startsWith("#") ? s.color : "#000000"} onChange={(e) => c().setColor(e.target.value).run()} className="absolute inset-0 opacity-0 cursor-pointer" />
      </label>
      {btn(s.hl, () => c().toggleHighlight().run(), Highlighter, "highlight")}
      {sep}
      {btn(s.align === "left", () => c().setTextAlign("left").run(), AlignLeft, "left")}
      {btn(s.align === "center", () => c().setTextAlign("center").run(), AlignCenter, "center")}
      {btn(s.align === "right", () => c().setTextAlign("right").run(), AlignRight, "right")}
      {btn(s.align === "justify", () => c().setTextAlign("justify").run(), AlignJustify, "justify")}
      {sep}
      {btn(s.ul, () => c().toggleBulletList().run(), List, "ul")}
      {btn(s.ol, () => c().toggleOrderedList().run(), ListOrdered, "ol")}
      {btn(s.quote, () => c().toggleBlockquote().run(), Quote, "quote")}
      {btn(false, () => c().setHorizontalRule().run(), Minus, "hr")}
      {sep}
      {btn(s.link, addLink, Link2, "link")}
      {btn(false, onImage, ImagePlus, "image")}
      {btn(false, addYoutube, YtIcon, "youtube")}
      {btn(s.table, () => c().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run(), TableIcon, "table")}
      {s.table && <>
        {btn(false, () => c().addRowAfter().run(), Rows3, "row")}
        {btn(false, () => c().addColumnAfter().run(), Columns3, "col")}
        {btn(false, () => c().deleteTable().run(), Trash2, "deltable")}
      </>}
      {sep}
      {btn(false, () => c().unsetAllMarks().clearNodes().run(), Eraser, "clear")}
      <span className="flex-1" />
      {btn(false, () => c().undo().run(), Undo2, "undo")}
      {btn(false, () => c().redo().run(), Redo2, "redo")}
    </div>
  );
}

function Editor({ value, onChange, testid, minH }) {
  const fileRef = useRef();
  const editor = useEditor({
    extensions: [
      StarterKit.configure({ heading: { levels: [1, 2, 3, 4] }, link: { openOnClick: false } }),
      Image, Youtube.configure({ nocookie: true }), TextAlign.configure({ types: ["heading", "paragraph"] }),
      Highlight, TextStyleKit, TableKit.configure({ table: { resizable: false } }),
    ],
    content: value || "",
    editorProps: { attributes: { class: `rich-content p-4 focus:outline-none ${minH}` } },
    onUpdate: ({ editor }) => onChange(editor.getHTML()),
  });
  if (!editor) return null;
  const upload = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/admin/upload", fd);
      editor.chain().focus().setImage({ src: data.url, alt: data.alt }).run();
    } catch (e) { toast.error(errMsg(e)); }
    fileRef.current.value = "";
  };
  return (
    <div className="border rounded-lg bg-white overflow-hidden">
      <Toolbar editor={editor} testid={testid} onImage={() => fileRef.current.click()} />
      <input ref={fileRef} type="file" accept="image/jpeg,image/png" className="hidden" onChange={(e) => upload(e.target.files[0])} />
      <EditorContent editor={editor} data-testid={testid} />
    </div>
  );
}

export function SingleRichEditor({ value, onChange, testid = "rich-editor-single", minH = "min-h-[220px]" }) {
  return <Editor value={value} onChange={onChange} testid={testid} minH={minH} />;
}

export default function RichEditor({ label, value = {}, onChange, testid = "rich-editor", minH = "min-h-[260px]" }) {
  const [lang, setLang] = useState("tr");
  const [version, setVersion] = useState(0);
  const { busy, run } = useTranslate();
  const translate = async () => {
    const r = await run(value.tr, lang);
    if (r) { onChange({ ...value, [lang]: r }); setVersion(version + 1); }
  };
  return (
    <Field label={label}>
      <LangTabs lang={lang} setLang={setLang} value={value} onTranslate={translate} busy={busy} testid={testid} />
      <Editor key={`${lang}-${version}`} value={value[lang]} onChange={(h) => onChange({ ...value, [lang]: h })} testid={testid} minH={minH} />
    </Field>
  );
}
